import { ORBIT_SETTLED } from '@/theme/colors';
import { Box, Button, Checkbox, Group, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useState } from 'react';

/** What a coverage button did, in place of the button once done. */
export function VerificationTaskDone({ text }: { text: string }) {
  return (
    <Group gap={4} wrap="nowrap" py={6}>
      <IconCheck size={14} color={ORBIT_SETTLED} />
      <Text size="sm" fw={500} c={ORBIT_SETTLED}>
        {text}
      </Text>
    </Group>
  );
}

interface VerificationTaskButtonProps {
  /** "Move to Employer track and create verification task", or "Create verification task". */
  label: string;
  /** Shown in place of the button when it can't run, e.g. no payments role. */
  unavailableText?: string;
  /** The Switch track dialog's acknowledgement, for a move that scrubs a Close record. */
  acknowledgementLabel?: string;
  /** Resolves once done; a rejection is shown and the button stays. */
  onRun: (acknowledged: boolean) => Promise<void>;
  loading?: boolean;
}

/**
 * The coverage sections' next-step button (BAR-2921), shared by the employer
 * and insurance sections. Knows nothing about tracks or tasks: the card says
 * what it does and runs it.
 */
export function VerificationTaskButton({
  label,
  unavailableText,
  acknowledgementLabel,
  onRun,
  loading = false,
}: VerificationTaskButtonProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | undefined>();

  if (unavailableText) {
    return (
      <Text size="sm" c="dimmed" py={6}>
        {unavailableText}
      </Text>
    );
  }

  const blocked = Boolean(acknowledgementLabel) && !acknowledged;

  return (
    <Box py={6}>
      {acknowledgementLabel && (
        <Checkbox
          size="xs"
          mb={6}
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.currentTarget.checked)}
          label={<Text size="xs">{acknowledgementLabel}</Text>}
        />
      )}
      <Button
        size="compact-sm"
        color="green.9"
        loading={running || loading}
        disabled={blocked}
        onClick={async () => {
          setRunning(true);
          setError(undefined);
          try {
            await onRun(acknowledged);
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Something went wrong');
          } finally {
            setRunning(false);
          }
        }}
      >
        {label}
      </Button>
      {error && (
        <Text size="xs" c="red.7" pt={4}>
          {error}
        </Text>
      )}
    </Box>
  );
}
